import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, NavigationSkipped, Router } from '@angular/router';
import { filter, map, withLatestFrom } from 'rxjs';
import { EventBusService } from 'shared/services/event-bus.service';
import { ModalService } from 'shared/services/modal.service';
import { TooltipService } from './tooltip.service';

@Injectable({
    providedIn: 'root',
})
export class RouteHelperService {
    constructor(
        private router: Router,
        private modalService: ModalService,
        private tooltipService: TooltipService,
        private destroyRef: DestroyRef,
        private eventBus: EventBusService,
    ) {}

    public getFinalRouteData() {
        // We need NavigationSkipped as well in order to detect when the user moves between listing feature groups tabs, since that's not a router event
        const filteredNavigationInstances = this.router.events.pipe(
            takeUntilDestroyed(this.destroyRef),
            filter((event) => event instanceof NavigationEnd || event instanceof NavigationSkipped),
        );

        filteredNavigationInstances
            .pipe(takeUntilDestroyed(this.destroyRef), withLatestFrom(this.eventBus.getObservableForEventStream('modalOpen')))
            .subscribe(([navigationEvent, isAnyModalOpen]) => {
                if (isAnyModalOpen) {
                    this.modalService.closeAllModals();
                }

                if (this.tooltipService.isTooltipOpen()) {
                    this.tooltipService.hideTooltip();
                }
            });

        return filteredNavigationInstances.pipe(
            filter((event) => event instanceof NavigationEnd),
            map(() => {
                let route: ActivatedRoute = this.router.routerState.root;

                while (route!.firstChild) {
                    route = route.firstChild;
                }

                return route.snapshot.data;
            }),
        );
    }

    public isOnListingEditPage(listingId: string | null = null) {
        const routeParts = this.router.url.split('/').filter((part) => part);

        if (routeParts.length < 3) {
            return false;
        }

        // Check if route is <host>/listings/details
        if (routeParts[0].toLowerCase() !== 'listings' || routeParts[1].toLowerCase() !== 'details') {
            return false;
        }

        // Exclude visuals routes - they are not considered listing edit pages
        if (routeParts[2].toLowerCase() === 'visuals') {
            return false;
        }

        // Check if listing ID matches, if provided
        if (!listingId) {
            return true;
        }

        // For routes with 'content' in the path
        // Route: listings/details/content/{listingId}/...
        if (routeParts[2].toLowerCase() === 'content') {
            return routeParts[3]?.toLowerCase() === listingId.toLowerCase();
        }

        // For routes without 'content' in the path
        // Route: listings/details/{listingId}/...
        return routeParts[2]?.toLowerCase() === listingId.toLowerCase();
    }

    public getListingFeatureGroupNameFromRoute(): string {
        const routeParts = this.router.url.split('/').filter((part) => part);

        if (routeParts.length < 5) {
            return '';
        }

        // Route is of format <host>/listings/details/<listingId>/<featureGroupName> without content in path
        if (routeParts[0].toLowerCase() !== 'listings' || routeParts[1].toLowerCase() !== 'details') {
            return '';
        }

        if (routeParts[2].toLowerCase() === 'content') {
            // On outlet, route is of format <host>/listings/details/content/<listingId>/outlet/<outletId>/outletProfile
            if (routeParts[4].toLowerCase() === 'outlet' && routeParts.length >= 7) {
                return routeParts[6];
            }
            // On listing, route is of format <host>/listings/details/content/<listingId>/<featureGroupName>
            return routeParts[4];
        }
        // On outlet, route is of format <host>/listings/details/<listingId>/outlet/<outletId>/outletProfile
        if (routeParts[3].toLowerCase() === 'outlet' && routeParts.length >= 6) {
            return routeParts[5];
        }

        // Route is of format <host>/listings/details/<listingId>/<featureGroupName>
        return routeParts[3];
    }
}
